import { createSseClient } from '@piaas/piaas-ui-toolkit/sse';

import Caas from '../../../../../api/caas';
import http from '../../../../../api/httpClient';

import type { ClusterUpgradeStatusItem, NodeGroupStatus } from '@/types/caasTypes';

const CLUSTER_STREAM_CLEANUP_DEBOUNCE_MS = 500;

/**
 * Wire shape `piaas-api-caas`'s `/cluster/upgrade-status/stream` actually sends
 * (see its `UpgradeProgressEvent`) - flat, not the nested `ClusterUpgradeStatusItem`
 * shape the REST endpoint returns. Mapped to `ClusterUpgradeStatusItem` below so
 * every downstream consumer only ever deals with one shape.
 */
interface UpgradeProgressEvent {
  clusterName?: string;
  targetVersion?: string;
  oldVersion?: string;
  finished: boolean;
  postCheck?: string;
  masters?: NodeGroupStatus;
  workers?: Record<string, NodeGroupStatus>;
  functions?: Record<string, unknown>;
  triggeredAt?: string;
}

/**
 * Only `event: progress` is handled - `event: end` carries a reduced summary
 * (`{ seq, state, reason }`, no status fields at all) sent immediately after the
 * final `progress` frame that already has the complete terminal state, so it adds
 * nothing to map. The backend closes the connection right after `end`; the toolkit
 * client (built on raw fetch, not `EventSource`) has no notion of "closed on
 * purpose, don't reconnect" and will retry a few times before falling back to
 * polling - harmless, since the UI already has the correct final state by then.
 */
const mapUpgradeProgressEventToStatusItem = (
  event: UpgradeProgressEvent
): ClusterUpgradeStatusItem => ({
  spec: {
    ...(event.clusterName !== undefined && { clusterName: event.clusterName }),
    ...(event.targetVersion !== undefined && { newVersion: event.targetVersion }),
    ...(event.oldVersion !== undefined && { oldVersion: event.oldVersion }),
    ...(event.triggeredAt !== undefined && { triggeredAt: event.triggeredAt }),
  },
  status: {
    finished: event.finished,
    ...(event.postCheck !== undefined && { postCheck: event.postCheck }),
    ...(event.masters !== undefined && { masters: event.masters }),
    ...(event.workers !== undefined && { workers: event.workers }),
    ...(event.functions !== undefined && { functions: event.functions }),
  },
  ...(event.triggeredAt !== undefined && { triggeredAt: event.triggeredAt }),
});

interface StreamCallbacks {
  onStatus: (item: ClusterUpgradeStatusItem) => void;
  onUnavailable: () => void;
}

interface ClusterStream {
  client: ReturnType<typeof createSseClient<UpgradeProgressEvent>>;
  subscribers: Set<StreamCallbacks>;
  cleanupTimeoutId: number | undefined;
}

const streams = new Map<string, ClusterStream>();

const createClusterStream = (
  clusterName: string,
  subscribers: Set<StreamCallbacks>
): ClusterStream => {
  const client = createSseClient<UpgradeProgressEvent>({
    connect: signal =>
      http
        .get<ReadableStream<Uint8Array>>(Caas.streamClusterUpgradeStatusUrl(clusterName), {
          adapter: 'fetch',
          headers: { Accept: 'text/event-stream' },
          responseType: 'stream',
          signal,
          fetchOptions: { cache: 'no-store', credentials: 'same-origin' },
        })
        .then(response => response.data),
    events: ['progress'],
    onEvent: (_eventName, payload) => {
      const statusItem = mapUpgradeProgressEventToStatusItem(payload);

      subscribers.forEach(subscriber => {
        try {
          subscriber.onStatus(statusItem);
        } catch (error) {
          console.error('Cluster upgrade status subscriber threw:', error);
        }
      });
    },
    onUnavailable: () => {
      subscribers.forEach(subscriber => {
        try {
          subscriber.onUnavailable();
        } catch (error) {
          console.error('Cluster upgrade unavailable subscriber threw:', error);
        }
      });
    },
    // Matches this stream's original retry budget - the toolkit's own
    // defaults for delay/backoff/jitter already match what this used too.
    reconnect: { maxAttempts: 3 },
  });

  return { client, subscribers, cleanupTimeoutId: undefined };
};

export const subscribeToClusterUpgradeStream = (
  clusterName: string,
  callbacks: StreamCallbacks
): (() => void) => {
  let stream = streams.get(clusterName);

  if (!stream) {
    const subscribers = new Set<StreamCallbacks>();
    stream = createClusterStream(clusterName, subscribers);
    streams.set(clusterName, stream);
  }

  const currentStream = stream;

  if (currentStream.cleanupTimeoutId !== undefined) {
    window.clearTimeout(currentStream.cleanupTimeoutId);
    currentStream.cleanupTimeoutId = undefined;
  }

  currentStream.subscribers.add(callbacks);
  const unsubscribeFromClient = currentStream.client.subscribe();

  return () => {
    currentStream.subscribers.delete(callbacks);
    unsubscribeFromClient();

    if (currentStream.subscribers.size === 0 && currentStream.cleanupTimeoutId === undefined) {
      // Debounced, mirroring the toolkit's own connection-close debounce -
      // deleting this entry immediately would make a rapid
      // unsubscribe-then-resubscribe (e.g. React remounting) create a brand
      // new client (and a second, briefly-overlapping connection) instead of
      // reusing the one the toolkit is already keeping alive for exactly
      // this case.
      currentStream.cleanupTimeoutId = window.setTimeout(() => {
        currentStream.cleanupTimeoutId = undefined;

        if (currentStream.subscribers.size > 0) {
          return;
        }

        streams.delete(clusterName);
      }, CLUSTER_STREAM_CLEANUP_DEBOUNCE_MS);
    }
  };
};
